'use client'

import Script from 'next/script'
import {MessageCircle} from 'lucide-react'
import {useEffect, useState} from 'react'
import {t} from '@/lib/i18n'

declare global {
  interface Window {
    FrontkomChatWidget?: {
      init: (config: {
        apiKey: string
        language?: string
        widgetAlignment?: 'left' | 'right'
      }) => void
      destroy?: () => void
      show?: () => void
    }
  }
}

const CHAT_WIDGET_SRC = 'https://ai-jotul-chat.vercel.app/widget.js'
const CHAT_CONTAINER_ID = 'frontkom-chat-container'
const CHAT_TOOLTIP_STYLE_ID = 'jotul-manuals-chat-tooltip-override'
const isManualsChatbotEnabled = (value = process.env.NEXT_PUBLIC_MANUALS_CHATBOT_ENABLED) =>
  !['false', '0', 'off'].includes(
    String(value || '')
      .trim()
      .toLowerCase(),
  )

type ManualsChatWidgetProps = {
  locale?: string
}

// The widget translates its UI for exactly these codes (verified against the live
// widget 2026-09-30, JO2-202). Any other value, a full locale like `nb-NO`
// included, renders English text with a Norwegian placeholder, so only a bare
// supported code may be passed.
const CHAT_LANGUAGES = new Set([
  'no',
  'en',
  'sv',
  'da',
  'de',
  'fr',
  'fi',
  'es',
  'it',
  'nl',
  'pl',
  'cs',
])

export const chatLanguageForLocale = (locale?: string) => {
  const language = locale?.split('-')[0]?.toLowerCase()
  if (language === 'nb' || language === 'nn') return 'no'
  return language && CHAT_LANGUAGES.has(language) ? language : 'en'
}

const openChatWidget = () => {
  window.FrontkomChatWidget?.show?.()

  window.setTimeout(() => {
    const container = document.getElementById(CHAT_CONTAINER_ID)
    const launcherRoot = container?.shadowRoot ?? document
    const launcher = launcherRoot.querySelector<HTMLButtonElement>(
      '[data-test-id="chat-icon-button"], button[aria-label="Open chat widget"]',
    )

    launcher?.click()
  }, 0)
}

const hideHardcodedWidgetTooltip = () => {
  const container = document.getElementById(CHAT_CONTAINER_ID)
  const shadowRoot = container?.shadowRoot

  if (!shadowRoot || shadowRoot.getElementById(CHAT_TOOLTIP_STYLE_ID)) return

  const style = document.createElement('style')
  style.id = CHAT_TOOLTIP_STYLE_ID
  style.textContent = '.availability-tooltip { display: none !important; }'
  shadowRoot.appendChild(style)
}

export function ManualsChatWidget(props: ManualsChatWidgetProps) {
  if (!isManualsChatbotEnabled()) return null

  return <ManualsChatWidgetInner {...props} />
}

function ManualsChatWidgetInner({locale}: ManualsChatWidgetProps) {
  const language = chatLanguageForLocale(locale)
  const [scriptReady, setScriptReady] = useState(false)

  useEffect(() => {
    if (!scriptReady || !window.FrontkomChatWidget) return

    window.FrontkomChatWidget.destroy?.()
    window.FrontkomChatWidget.init({
      apiKey: 'test-api-key',
      language,
      widgetAlignment: 'right',
    })

    window.requestAnimationFrame(hideHardcodedWidgetTooltip)
  }, [language, scriptReady])

  return (
    <>
      <Script
        id="frontkom-chat-widget-script"
        src={CHAT_WIDGET_SRC}
        strategy="afterInteractive"
        onReady={() => setScriptReady(true)}
      />
      <button
        type="button"
        onClick={openChatWidget}
        className="inline-flex min-h-[45px] shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border border-primary bg-primary/10 px-6 py-2 text-left text-primary shadow-sm transition-colors hover:bg-primary/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <MessageCircle className="size-4" aria-hidden="true" />
        <span className="flex flex-col leading-tight">
          <span className="text-sm font-semibold">
            {t(locale, 'manuals.chat.title', 'Get help with manuals')}
          </span>
          <span className="text-[0.7rem] font-medium text-foreground/70">
            {t(locale, 'manuals.chat.subtitle', 'Talk to our AI chatbot')}
          </span>
        </span>
      </button>
    </>
  )
}
